import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
/**
 * The voyage map — `VoyageHero` / `VoyageMap` / `ActiveStopCard` in the design.
 *
 * Recorded as blocked for four rounds on the grounds that "no shape exists".
 * `GET /api/journey/voyage` did exist; round 14 is where we found out. Worth
 * saying plainly, because the cost of that was the product's single most
 * distinctive screen sitting unbuilt while we asked for something already
 * shipped.
 *
 * ── There are no coordinates, and there should not be ─────────────────────
 * The prototype's fixture carries an `x`/`y` per stop and we very nearly asked
 * for them. The backend's answer is the right one: a map's geometry is our
 * layout, not their data. Coordinates on the wire would freeze a 1200×460
 * viewBox into the contract and make the mobile map a second endpoint.
 *
 * So `day` is the only positional fact, and the curve is ours. See
 * `voyage-map.tsx`, where a stop's place is its day's distance along the path.
 */
/*
 * ── Unknown values degrade; they do not take the map down ─────────────────
 * The backend adds stops over time (26 Sep 2026: `moduleCompleted`, `post`,
 * `anchor`, `storm`, `yearBegan`, plus `count` and `roomId`). A strict enum
 * here turned a key this build had never heard of into a ContractError for
 * the WHOLE voyage — and `VoyageHero` answers an error by drawing nothing. So
 * `kind` falls back to the neutral `moment` mark, `key` is kept as sent and
 * the copy table decides (`isKnownStopKey`), and new optional fields are
 * optional. The known sets stay enums so a screen can still switch on them.
 */
export const VoyageStopKind = z.enum(['milestone', 'moment', 'course', 'challenge']);
export const VOYAGE_STOP_KEYS = [
    'joined',
    'yearMark',
    'yearBegan',
    'courseStarted',
    'courseCompleted',
    'moduleCompleted',
    'firstPost',
    'post',
    'firstAnchor',
    'anchor',
    'storm',
    'challengePeriod',
];
export const VoyageStopKey = z.enum(VOYAGE_STOP_KEYS);
/** Whether this build has copy (and a destination rule) for a stop's key. */
export function isKnownStopKey(key) {
    return VOYAGE_STOP_KEYS.includes(key);
}
export const VoyageStopState = z.enum(['done', 'current', 'upcoming']);
export const VoyageStop = z.object({
    /** 1-based. The only positional fact on the wire. */
    day: z.number().int(),
    /** An unknown kind draws as a `moment` rather than failing the voyage. */
    kind: VoyageStopKind.catch('moment'),
    /**
     * WHICH stop this is, as opposed to what family it belongs to.
     *
     * `kind` groups for colour; `key` is what a label can be written against. It
     * matters because `title` is nullable — a course stop whose course has since
     * been unpublished still happened, and "Day 47 · Course" is a worse answer
     * than "Day 47 · Course started". The key always says something true.
     *
     * A plain string on purpose — see the note above. Narrow it with
     * `isKnownStopKey` before switching on it.
     */
    key: z.string(),
    state: VoyageStopState,
    /**
     * The row behind it — a course's content item id (`courseStarted`,
     * `courseCompleted`, `moduleCompleted`), a post's id (`firstPost`, `post`,
     * `firstAnchor`, and `anchor` when it anchors a post), a content item's id
     * (`anchor` on content), a challenge period's id. `null` on a milestone and
     * on `storm`.
     */
    refId: z.string().uuid().nullable(),
    title: z.string().nullable(),
    /**
     * The room a post stop was written in — the thread route needs it, and the
     * post id alone cannot be opened. Nullable, and absent from older servers.
     */
    roomId: z.string().uuid().nullish(),
    /**
     * For a stop about a piece of content — an `anchor` on an article, say — the
     * path segment the API serves it under (`courses`, `podcasts`), which
     * `routePathForContent` turns into our route. Null for everything else.
     */
    contentPath: z.string().nullish(),
    /**
     * How many of this key fell on this day (≥ 1). The server collapses the same
     * key on the same day into one stop; absent from older servers means one.
     */
    count: z.number().int().positive().optional(),
});
export const Voyage = z.object({
    /**
     * Which year aboard this map is — 1 for the first (25 Sep 2026). The map is
     * the CURRENT year: a member who joined three years ago sees year 3, and
     * `today`, the stops and `startedOn` are all counted from its day 1.
     * Defaulted to 1 for a server predating it, which only ever drew year one.
     */
    year: z.number().int().positive().default(1),
    /**
     * Day 1 of this year's map, in Montréal — `joinedOn` in year 1. Optional for
     * the same reason; read it through `voyageStartedOn`.
     */
    startedOn: z.string().nullish(),
    /**
     * 1-based and **NOT clamped** to `totalDays` — the backend says so outright.
     *
     * A member past their year-mark reports day 400 of 365. That is a real state
     * and the number is theirs, so it renders as sent; only the geometry clamps,
     * or the boat sails off the end of the path. Assuming `today <= totalDays`
     * would have put it there on day 366.
     */
    today: z.number().int(),
    totalDays: z.number().int().positive(),
    joinedOn: z.string(),
    yearMarkOn: z.string(),
    stops: z.array(VoyageStop),
    locale: z.string().optional(),
});
/** Day 1 of the map this voyage draws — this year's, not the join date's. */
export function voyageStartedOn(voyage) {
    return voyage.startedOn ?? voyage.joinedOn;
}
export function useVoyage(http) {
    return useQuery({
        queryKey: ['journey', 'voyage'],
        queryFn: () => http.get('/journey/voyage', { schema: Voyage }),
        /* A voyage moves once a day at most. */
        staleTime: 5 * 60_000,
    });
}
/**
 * Stories — the log beside the map.
 *
 * ── `unavailable` is not an error list ────────────────────────────────────
 * `milestone` is declared in `kind` and **never returned**; it comes back in
 * `unavailable` with a reason instead. That distinction is the whole point of
 * the field: "we cannot compute this yet" and "you have none" are different
 * things to tell somebody about their own history, and a plain empty list says
 * the second when it means the first.
 */
export const StoryKind = z.enum(['post', 'note', 'milestone']);
export const Story = z.object({
    kind: StoryKind,
    day: z.number().int(),
    at: z.string(),
    body: z.string(),
    postId: z.string().uuid().nullish(),
    roomId: z.string().uuid().nullish(),
    contentItemId: z.string().uuid().nullish(),
    loggedFor: z.string().nullish(),
    periodId: z.string().uuid().nullish(),
});
export const StoryFeed = z.object({
    items: z.array(Story),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    /** Kinds that could not be computed, and why. Rendered, not swallowed. */
    unavailable: z.array(z.object({ kind: StoryKind, reason: z.string() })).nullish(),
});
export function useStories(http, page = 1) {
    return useQuery({
        queryKey: ['journey', 'stories', page],
        queryFn: () => http.get('/journey/stories', { schema: StoryFeed, query: { page, pageSize: 20 } }),
    });
}
//# sourceMappingURL=voyage.js.map